import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'; // BrowserRouterをRouterにエイリアス指定
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';

import LandingPage from './Pages/LP/LandingPage';
import Login from './Pages/Login/Login';
import Register from './Pages/Register/Register';
import Logout from './Pages/Logout/Logout';
import Home from './Pages/Home/Home';
import StoreManagement from './Pages/StoreManagement/StoreManagement';
import StripeConnectCallback from './Pages/StoreManagement/StripeConnectCallback';
import RequireManager from './Components/Auth/RequireManager';
import RequireActiveSubscription from './Components/Auth/RequireActiveSubscription';
import SessionGuard from './Components/Auth/SessionGuard';
import SelectStore from './Pages/SelectStore/SelectStore';
import DiningTable from './Pages/DiningTable/DiningTable';
import Menu from './Pages/Menu/Menu';
import MenuPreview from './Pages/Menu/MenuPreview'; // 新しいプレビューページをインポート
import MenuOptionManagement from './Pages/MenuOption/MenuOptionManagement'; // メニューオプション管理画面をインポート
import ActiveStore from './Pages/Order/ActiveStore';
import ActiveDiningTableSitting from './Pages/Public/ActiveDiningTableSitting'; // 新しいコンポーネントをインポート
import Order from './Pages/Public/Order'; // Orderコンポーネントをインポート
import PublicOrderError from './Pages/Public/PublicOrderError';
import ReservationManagement from './Pages/Reservation/ReservationManagement';
import ReservationDetail from './Pages/Reservation/ReservationDetail';
import ReservationNew from './Pages/Reservation/ReservationNew';
import ReservationSettings from './Pages/Reservation/Settings/ReservationSettings';
import ReservationPreview from './Pages/Reservation/ReservationPreview';
import PublicReservation from './Pages/PublicReservation/PublicReservation';
import ReservationLookup from './Pages/PublicReservation/ReservationLookup';
import PublicPrivacyPolicy from './Pages/PublicReservation/PublicPrivacyPolicy';

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      {/* デザインシステムの背景色（--bg-light）等をアプリ全体へ適用する。ADR 0029 */}
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/select-store" element={<SelectStore />} />
          {/* 認証済みスタッフ画面。SessionGuard で削除済みユーザーを検出して即時ログアウトする。 */}
          <Route element={<SessionGuard />}>
            <Route path="/home" element={<Home />} />
            <Route path="/:store_id/manage/store" element={<StoreManagement />} />
            {/* 旧 店員管理画面のパス。店舗管理配下へ統合済みのため後方互換でリダイレクト相当のマウントを残す。 */}
            <Route path="/:store_id/manage/clerks" element={<StoreManagement />} />
            {/* Stripe Connect OAuth コールバック。redirect URI は完全一致登録制のため店舗 ID を含まない固定パスで受ける。ADR 0039 */}
            <Route path="/stripe/connect/callback" element={<StripeConnectCallback />} />
            {/* メニュー管理・テーブル管理は管理者のみ。店員(CLERK)はアクセス不可。 */}
            <Route path="/:store_id/manage/dining-table" element={<RequireManager><DiningTable /></RequireManager>} />
            <Route path="/:store_id/manage/menu" element={<RequireManager><Menu /></RequireManager>} />
            <Route path="/:store_id/manage/menu-options" element={<RequireManager><MenuOptionManagement /></RequireManager>} />
            {/* 店内注文管理は有効なサブスク（entitled）が無い場合は利用不可。INACTIVE/EXPIRED 等で抑止。 */}
            <Route path="/:store_id/manage/order" element={<RequireActiveSubscription><ActiveStore /></RequireActiveSubscription>} />
            <Route path="/:store_id/menu/preview" element={<MenuPreview />} />
            {/* 予約管理。サブスク切れでも既存予約の処理（来店客対応）ができる必要があるため、
                RequireActiveSubscription は意図的に掛けない（SessionGuard のみ・CLERK 可）。計画 FE Phase 1 */}
            <Route path="/:store_id/manage/reservation" element={<ReservationManagement />} />
            <Route path="/:store_id/manage/reservation/new" element={<ReservationNew />} />
            <Route path="/:store_id/manage/reservation/:reservation_id" element={<ReservationDetail />} />
            {/* 予約設定ハブは管理者のみ（他マスタ設定と同様）。サブスクゲートなし */}
            <Route path="/:store_id/manage/reservation-settings" element={<RequireManager><ReservationSettings /></RequireManager>} />
            {/* 公開予約フォームのプレビュー（MenuPreview と同型・送信無効） */}
            <Route path="/:store_id/reservation/preview" element={<ReservationPreview />} />
          </Route>
          <Route path="/public/:store_id/:dining_table_id/sitting" element={<ActiveDiningTableSitting />} />
          <Route path="/public/active/:active_store_id/:active_dining_table_id/order" element={<Order />} />
          <Route path="/public/active/:active_store_id/:active_dining_table_id/order/by-clerk/:clerk_id" element={<Order />} />
          <Route path="/public/error" element={<PublicOrderError />} />
          {/* 公開予約（認証不要）。/reservations は照会/セルフキャンセル（?no=&token=&store=） */}
          <Route path="/reservations/:store_id" element={<PublicReservation />} />
          {/* 店舗の個人情報の取り扱い（privacyPolicyType=TEXT の店舗の掲載本文・フィードバック3） */}
          <Route path="/reservations/:store_id/privacy" element={<PublicPrivacyPolicy />} />
          <Route path="/reservations" element={<ReservationLookup />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;